import {Suspense, useLayoutEffect} from "react"
import {BrowserRouter, Route, Routes, useLocation} from "react-router-dom"
import {QueryClient, QueryClientProvider} from "@tanstack/react-query"
import {ReactQueryDevtools} from "@tanstack/react-query-devtools"

import {OptOutPage} from "./OptOutPage"
import {TransactionalEmailOptOutPage} from "./TransactionalEmailOptOutPage"
import {NotificationOfAssignmentAcknowledgementPage} from "./NotificationOfAssignmentAcknowledgementPage"
import {GrantESIGNConsentPage} from "./GrantESIGNConsentPage"
import {PaymentPlanStatusWithRegionPage} from "./Pages/PaymentPlanStatusPage"
import {Pages} from "./Pages"
import {LocaleLoader} from "./LocaleLoader"
import {CustomerInfoTracker} from "./CustomerInfoTracker"
import "./i18n"
import {RegionWrapper} from "./RegionProvider"

import {AppThemeProvider} from "@indebted/theme"
import {LoadingPage} from "@indebted/components/Loading"
import {PaymentArrangementRouter} from "@indebted/payment-arrangement"
import {AnalyticsProvider} from "@indebted/analytics"
import {NotificationProvider} from "@indebted/components/Notification"
import {Layout} from "@indebted/components/Layout"
import {InDebtedCoRoute} from "@indebted/components/InDebtedCoRoute"
import {NotFoundPage} from "@indebted/components/Error"
import {ApiError} from "@indebted/api/ApiError"

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: (failureCount, error) => {
				if (error instanceof ApiError && error.Status === 404) {
					return false
				}
				return failureCount < 3
			},
		},
	},
})

const InnerApp = () => {
	return (
		<Suspense fallback={<LoadingPage />}>
			<QueryClientProvider client={queryClient}>
				<AnalyticsProvider>
					<NotificationProvider>
						<BrowserRouter>
							<RegionWrapper>
								<LocaleLoader>
									<ScrollToTopFix />
									<Layout>
										<AppRoutes />
									</Layout>
								</LocaleLoader>
								<CustomerInfoTracker />
							</RegionWrapper>
						</BrowserRouter>
					</NotificationProvider>
				</AnalyticsProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</Suspense>
	)
}

const App = () => {
	return (
		<AppThemeProvider>
			<InnerApp />
		</AppThemeProvider>
	)
}

const AppRoutes = () => (
	<Routes>
		<Route element={<InDebtedCoRoute />} path="/" />

		<Route element={<OptOutPage />} path="/opt-out/:token/:channel/*" />
		<Route element={<TransactionalEmailOptOutPage />} path="/transactional-email-opt-out/:token" />
		<Route element={<NotificationOfAssignmentAcknowledgementPage />} path="/accounts/ack-noa" />
		<Route element={<GrantESIGNConsentPage />} path="customers/grant-esign-consent/*" />
		<Route element={<GrantESIGNConsentPage />} path="customers/:regionPrefix/grant-esign-consent/*" />
		<Route element={<PaymentPlanStatusWithRegionPage region={0} />} path="/0/payment-plan/:paymentPlanID" />
		<Route element={<PaymentPlanStatusWithRegionPage region={1} />} path="/1/payment-plan/:paymentPlanID" />
		<Route element={<PaymentPlanStatusWithRegionPage region={2} />} path="/2/payment-plan/:paymentPlanID" />
		<Route element={<PaymentArrangementRouter />} path="/:region/payment-arrangement/*" />
		<Route element={<Pages />} path="/:secureCode/*" />
		<Route element={<NotFoundPage />} path="*" />
	</Routes>
)

const ScrollToTopFix = () => {
	const location = useLocation()

	useLayoutEffect(() => {
		// Scroll to the top of the page when the route changes
		window.scrollTo({top: 0, left: 0, behavior: "instant"})
	}, [location.pathname])

	return null
}

export {App, AppRoutes}
